import React from 'react';
import styled from 'styled-components';

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--background-secondary);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-primary);
  box-shadow: var(--shadow-sm);
  flex-wrap: wrap;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 16px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
  
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const FilterLabel = styled.label`
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-md);
  background: var(--background-secondary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
  
  &:hover {
    border-color: var(--accent-primary);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const FilterInput = styled.input`
  padding: 12px 16px;
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-md);
  background: var(--background-secondary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px var(--primary-100);
  }
  
  &:hover {
    border-color: var(--accent-primary);
  }
  
  &::placeholder {
    color: var(--text-tertiary);
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    margin-left: 0;
    justify-content: stretch;
  }
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: var(--background-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--error-50);
    color: var(--error-600);
    border-color: var(--error-200);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    flex: 1;
  }
`;

const FilterIcon = styled.span`
  font-size: 16px;
  opacity: 0.7;
`;

export interface FilterOption {
  value: string | number;
  label: string;
}

interface FiltersProps {
  statusFilter?: number | string;
  searchTerm?: string;
  statusOptions: FilterOption[];
  onStatusChange: (status: number | string) => void;
  onSearchChange?: (search: string) => void;
  onClear: () => void;
}

export const Filters: React.FC<FiltersProps> = ({
  statusFilter,
  searchTerm,
  statusOptions,
  onStatusChange,
  onSearchChange,
  onClear
}) => {
  return (
    <FiltersContainer>
      <FilterGroup>
        <FilterLabel>
          <FilterIcon>📊</FilterIcon> Status Filter
        </FilterLabel>
        <FilterSelect
          value={statusFilter || ''}
          onChange={(e) => onStatusChange(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All Statuses</option>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FilterSelect>
      </FilterGroup>
      
      {onSearchChange && (
        <FilterGroup>
          <FilterLabel>
            <FilterIcon>🔍</FilterIcon> Search
          </FilterLabel>
          <FilterInput
            type="text"
            placeholder="Search by name, phone, doctor..."
            value={searchTerm || ''}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </FilterGroup>
      )}
      
      <ActionGroup>
        <ClearButton onClick={onClear}>
          <FilterIcon>✨</FilterIcon>
          Clear Filters
        </ClearButton>
      </ActionGroup>
    </FiltersContainer>
  );
};